import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, concat, debounceTime, distinctUntilChanged, filter, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import Constants from 'src/app/core/constants/constants';
import { IContactDetails } from 'src/app/core/models/contactDetails.interface';
import { IAddress, ICompany, ICompanyDetails, ICompanyType } from 'src/app/core/models/interface';
import { RestAPIsService } from 'src/app/core/services/rest-apis.service';
import { SessionService } from 'src/app/core/services/session.service';
import { UserService } from 'src/app/core/services/user.service';

@Component({
  selector: 'app-edit-company',
  templateUrl: './edit-company.component.html',
  styleUrls: ['./edit-company.component.scss']
})
export class EditCompanyComponent implements OnInit {
  @Input() modalRef!: NgbModalRef
  addresses$!: Observable<[]>;
  listOfCompanies: Array<ICompany> = [];
  companyForm: FormGroup;
  postcodeRegex: RegExp =
    /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/gm;
  isCompanyLoading: boolean = false;
  isCompanyAddressLoading: boolean = false;
  selectedCompanyObj: ICompanyDetails | null = null;
  isCompanyAddress: boolean = false;
  companyAddress: string = '';
  isManualCompany: boolean = false;
  isCompanyNoAddress: boolean = false;
  invalidAddress: string = '';
  isAddressLoading: boolean = false;
  listOfAddress: Array<IAddress> = [];
  isCompanyAddressNotInList: any;
  selectedAddressObj: any;
  isSelectedAddress: boolean = false;
  errorMessage: string = '';
  isSubmitted: boolean = false;
  successMessage: string = '';
  companyTypes: ICompanyType[] = Constants.CompanyType
  companies$!: Observable<ICompany[]>;
  companyInput$ = new Subject<string>();
  minLengthTerm = 3
  fileError = ''
  selectedFile: File | null = null;
  LOAFileConfig = Constants.LOAFileConfig
  isContactLoading = false;
  selectedAuthoriserPerson!: IContactDetails;
  listOfContacts: IContactDetails[] = [];
  listOfTitles: string[] = [
    'Mr',
    'Mrs',
    'Ms',
  ];
  isBrokerCompanyLoading = false;
  selectedBrokerCompany: any;
  companyList: Array<any> = [];
  selectedCompany: any

  constructor(private restAPI: RestAPIsService, private modalService: NgbModal,
    private sessionService: SessionService, private spinner: NgxSpinnerService,
    private userService: UserService
  ) {
    
    this.companyForm = new FormGroup({
      brokerCompanyName: new FormControl(null, [Validators.required]),
      companyName: new FormControl(null, [Validators.required]),
      companyNameManual: new FormControl(null),
      companyAddress: new FormControl(null, [Validators.required]),
      companyType: new FormControl(null, [Validators.required]),
      companyNumber: new FormControl(null, [Validators.required]),
      address: new FormGroup({
        locality: new FormControl(null),
        address_line_1: new FormControl(null),
        address_line_2: new FormControl(null),
        country: new FormControl(null),
        postal_code: new FormControl(null, [Validators.pattern(this.postcodeRegex)]),
      }),
      selectedGBGAddressId: new FormControl(''),
      title: new FormControl(null, [Validators.required]),
      firstName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.pattern(Constants.Regex.firstName)]),
      lastName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.pattern(Constants.Regex.lastName)]),
      phone: new FormControl('', [Validators.required, Validators.maxLength(11), Validators.pattern(Constants.Regex.Mobile)]),
      contactEmail: new FormControl('', [Validators.required, Validators.pattern(Constants.Regex.Email)]),
      contactSource: new FormControl('USER_PROVIDED'),
    });
    this.initCompanyList()
    
  }

  clearList(): void{
    this.initCompanyList()
  }

  ngOnInit(): void {
    this.getBrokerCompanies()
  }

  onBrokerCompanyChange(company: any): void {
    if (company) {
      if (company.companyName == '+ Add new company') {
        this.initCompanyList()
        this.companyForm.patchValue({
          companyType: null,
          companyNumber: null,
          companyAddress: null,
          companyName: null
        })
        this.addRequiredValidation(['companyName', 'companyNumber', 'companyAddress'])
      }
      else {
        this.selectedCompany = company;
        this.companyForm.patchValue({
          companyType: company?.companyType,
          companyNumber: company.companyNumber,
          companyAddress: company.companyAddress,
          companyName: company.companyName,
          companyNameManual: null
        })
        this.isManualCompany = false;
        this.getAllContacts()
      }
    }
    else {
      this.companyForm.patchValue({
        companyType: null,
        companyNumber: null,
        companyAddress: null,
        companyName: null
      })
      this.addRequiredValidation(['brokerCompanyName'])
    }
  }

  addRequiredValidation(controls: Array<string>): void {
    controls.forEach(el => {
      const control = this.companyForm.get(el)
      control?.setValidators([Validators.required])
      if (el == 'address.postal_code') {
        control?.setValidators([Validators.required, Validators.pattern(Constants.PostCodeRegExp)])
      }
      control?.updateValueAndValidity()
    })
  }

  removeRequiredValidation(controls: Array<string>): void {
    controls.forEach(el => {
      const control = this.companyForm.get(el)
      control?.clearValidators()
      control?.updateValueAndValidity()
    })
  }

  initCompanyList(): void{
    this.companies$ = concat(
      of([]),
      this.companyInput$.pipe(
        filter( res => res !== null && res.length >= this.minLengthTerm),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => this.isCompanyLoading = true),
        switchMap((company) => {
          const path = `carbon/v1/companies-by-text?searchQuery=${company}`;
          return this.restAPI.getAPI(path, 'domestic').pipe(
            map(resp => {
              const data = resp.data;
              data.push({ number: 'notInTheList', name: 'Company not in the list' })
              return data
            }),
            catchError(() => of([])),
            tap(() => this.isCompanyLoading = false)
          )
        })
      )
    )
  }

  trackByFn(item: any) {
    return item.number;
  }

  getFormControl(formControlName: string) {
    if (!formControlName) return null
    return this.companyForm.get(formControlName)
  }

  getErrorControl(formControlName: string) {
    if (!formControlName) return null
    return this.companyForm.get(formControlName)?.errors
  }

  /**
   * @description Search input address from API
   * @param {Object} e Example: `{term: "input"}` 
   */
  searchFreeText(e: any): void {
    if (e.term.length <= 2)
      return;
    this.isAddressLoading = true;
    this.listOfAddress = [];
    const path = `carbon/v3/free-text-address-search?address=${e.term}`;
    
    this.restAPI.getAPI(path, 'domestic').subscribe({
      next: (res) => {
        res?.data?.map((address: any) => {
          address['postcode'] =
            address.Description.split(',')[
              address.Description.split(',').length - 1
            ].trim();
          address['fullAddress'] = address.Text + ', ' + address.Description;
        });

        this.listOfAddress = res.data;
        this.listOfAddress.push({
          Id: 'notInTheList',
          fullAddress: 'Company address not in the list',
          Description: '',
          Highlight: '',
          Text: '',
          Type: ''
        })
        this.isAddressLoading = false;
        // Check GBG Address already there or not
        if (
          !!this.sessionService.getItemFromSession(
            Constants.sessionConstants.SELECTEDGBGADDRESS
          )
        ) {
          this.companyForm.patchValue({
            selectedGBGAddressId: this.sessionService.getItemFromSession(
              Constants.sessionConstants.SELECTEDGBGADDRESS
            ),
          });

          // Reinitialize the selected address
          this.selectedAddressObj = this.listOfAddress.find(
            (address) =>
              address.Id ===
              this.companyForm.controls['selectedGBGAddressId']?.value
          );
        }

        // Store address text
        this.sessionService.setItemIntoSession(
          Constants.sessionConstants.COMPANYADDRESS,
          this.companyForm.controls['companyAddress']?.value
        );
        // this.spinner.hide();
      },
      error: (err) => {
        console.log('ERROR: ', err);
        this.invalidAddress =
          err?.error?.error[0]?.message ||
          'Something went wrong. Please try again.';
        this.isAddressLoading = false;
        this.listOfAddress.push({
          Id: 'notInTheList',
          fullAddress: 'Company address not in the list',
          Description: '',
          Highlight: '',
          Text: '',
          Type: ''
        })
      },
    })
  }

  selectCompany(selectedCompany: any): void {
    const company = this.companyForm.controls['companyName']?.value
    if (company.number !== 'notInTheList') {
      this.companyForm.patchValue({
        companyName: selectedCompany.name,
        companyNameManual: null,
        companyAddress: null,
        companyType: selectedCompany.type,
        companyNumber: selectedCompany.number
      });
      this.companyForm.get('companyNameManual')?.removeValidators(Validators.required);
      this.companyForm.get('companyNameManual')?.updateValueAndValidity()
      this.selectedCompanyObj = selectedCompany
      this.isManualCompany = false;
      this.getCompanyAddress();
      this.getAllContacts();
    }
    else {
      // Not in the list
      this.companyForm.patchValue({
        companyName: 'Company not in the list',
        companyAddress: null,
        companyType: null,
        companyNumber: null
      });
      this.companyForm.get('companyNameManual')?.setValidators(Validators.required);
      this.companyForm.get('companyNameManual')?.updateValueAndValidity()
      this.isCompanyNoAddress = false;
      this.selectedCompanyObj = null
      this.isCompanyAddress = false;
      this.isCompanyLoading = false;
      this.isManualCompany = true;
    }
  }

  // Get all contacts
  getAllContacts() {
    const params = {
      customerEmail: this.sessionService.getItemFromSession(Constants.sessionConstants.EMAIL),
      companyNumber: this.companyForm.value.companyNumber
    };
    const path = 'carbon/v3/get-contact?' + new URLSearchParams(params).toString();
    this.isContactLoading = true;
    this.restAPI.getAPI(path, 'domestic').subscribe((response) => {
      if (response.code == 200) {
        this.listOfContacts = response?.data;

        // Add new person as a contact
        this.listOfContacts.splice(0, 0, {
          id: 0,
          title: '',
          firstName: '+ Add new person',
          lastName: '',
          phone: '',
          email: '',
        });
      } else {
        this.listOfContacts = [{
          id: 0,
          title: '',
          firstName: '+ Add new person',
          lastName: '',
          phone: '',
          email: '',
        }];
      }
      this.isContactLoading = false;
    }, error => {
      this.isContactLoading = false;
      this.listOfContacts = [{
        id: 0,
        title: '',
        firstName: '+ Add new person',
        lastName: '',
        phone: '',
        email: '',
      }];
    });
  }

  // Company address information from company details
  getCompanyAddress(): void {
    const searchParam = {
      type: this.selectedCompanyObj?.type || '',
      number: this.selectedCompanyObj?.number || ''
    }
    const path = `carbon/v1/company-details?${new URLSearchParams(searchParam).toString()}`;
    this.isCompanyAddressLoading = true;
    this.restAPI.getAPI(path, 'domestic').subscribe({
      next: (res) => {
        this.companyAddress = res.data[0].addressLine;
        this.isCompanyAddressLoading = false;
        this.isCompanyNoAddress = false;
        if (this.companyAddress) {
          this.isCompanyAddress = true;
          this.companyForm.patchValue({
            companyAddress: this.companyAddress,
          });
        } else {
          this.companyForm.patchValue({
            companyAddress: null,
          });
          this.isCompanyAddress = false;
        }
      },
      error: (err) => {
        console.log('ERROR: ', err);
        // when company not found in the company details list
        this.isManualCompany = true;
        this.isCompanyAddressLoading = false;
        this.isCompanyAddress = false;
        this.isCompanyNoAddress = true;
        this.companyForm.patchValue({
          companyAddress: null,
        });
      },
    });
  }

  // Selected address from dropdown
  selectedGBGAddress(address: any): void {
    if (address.Id !== "notInTheList") {
      this.selectedAddressObj = address;
      this.companyForm.patchValue({
        companyAddress: address.fullAddress,
        selectedGBGAddressId: address.Id,
      });

      this.isSelectedAddress = true;
      this.isCompanyAddressNotInList = false;
      
      this.companyForm.get('address')?.get('locality')?.removeValidators(Validators.required);
      this.companyForm.get('address')?.get('locality')?.updateValueAndValidity()
      this.companyForm.get('address')?.get('address_line_1')?.removeValidators(Validators.required);
      this.companyForm.get('address')?.get('address_line_1')?.updateValueAndValidity()
      this.companyForm.get('address')?.get('country')?.removeValidators(Validators.required);
      this.companyForm.get('address')?.get('country')?.updateValueAndValidity()
      this.companyForm.get('address')?.get('postal_code')?.clearValidators();
      this.companyForm.get('address')?.get('postal_code')?.updateValueAndValidity()
      
    } else {
      this.companyForm.patchValue({
        companyAddress: 'Company address not in the list',
        selectedGBGAddressId: 'notInTheList',
      });

      this.companyForm.get('address')?.get('locality')?.setValidators(Validators.required);
      this.companyForm.get('address')?.get('locality')?.updateValueAndValidity()
      this.companyForm.get('address')?.get('address_line_1')?.setValidators(Validators.required);
      this.companyForm.get('address')?.get('address_line_1')?.updateValueAndValidity()
      this.companyForm.get('address')?.get('country')?.setValidators(Validators.required);
      this.companyForm.get('address')?.get('country')?.updateValueAndValidity()
      this.companyForm.get('address')?.get('postal_code')?.setValidators([Validators.required, Validators.pattern(this.postcodeRegex)]);
      this.companyForm.get('address')?.get('postal_code')?.updateValueAndValidity()

      this.companyForm.get('address')?.markAllAsTouched()

      this.isCompanyAddressNotInList = true;

      // Store into session
      this.sessionService.setItemIntoSession(
        Constants.sessionConstants.COMPANYADDRESS,
        'Not in the list'
      );
      this.sessionService.setItemIntoSession(
        Constants.sessionConstants.SELECTEDGBGPOSTCODE,
        ''
      );
      this.sessionService.setItemIntoSession(
        Constants.sessionConstants.ADDRESSLINE,
        ''
      );
      this.sessionService.setItemIntoSession(
        Constants.sessionConstants.SELECTEDGBGADDRESS,
        'notInTheList'
      );
    }
  }

  // Form data submit for create user and LOA
  onSubmit(): void {
    this.errorMessage = '';

    // Check if form is valid and not already submitted
    if (this.companyForm.valid) {
      // this.spinner.show()
      const path = 'carbon/v1/add-user-data';
      const userDetails = this.sessionService.getItemFromSession(Constants.sessionConstants.USERDETAILS)
      let payload: any = {
        // firstname: userDetails.firstname,
        // email: userDetails.email,
        // phone: userDetails.phone,
        companyName: this.companyForm.get('companyName')?.value,
        companyAddress: this.companyForm.get('companyAddress')?.value,
        companyType: this.companyForm.get('companyType')?.value,
        companyNumber: this.companyForm.get('companyNumber')?.value,

        title: this.companyForm.get('title')?.value,
        firstName: this.companyForm.get('firstName')?.value,
        lastName: this.companyForm.get('lastName')?.value,
        phone: this.companyForm.get('phone')?.value,
        contactEmail: this.companyForm.get('contactEmail')?.value,
        contactSource: this.companyForm.get('contactSource')?.value,
        siteId: this.sessionService.getItemFromSession(Constants.sessionConstants.SELECTEDSITE)
      };

      if (this.companyForm.get('companyName')?.value == 'Company not in the list')
        payload['companyName'] = this.companyForm.get('companyNameManual')?.value;

      if (this.companyForm.get('companyAddress')?.value == 'Company address not in the list') {
        const address = [];
        address.push(
          this.companyForm.get('address.address_line_1')?.value,
          this.companyForm.get('address.address_line_2')?.value,
          this.companyForm.get('address.locality')?.value,
          this.companyForm.get('address.country')?.value,
          this.companyForm.get('address.postal_code')?.value);
        payload['companyAddress'] = address.join(',');
      }
      console.log(payload)

      // API call to submit the form data
      this.restAPI.postAPI(path, payload, 'domestic').subscribe({
        next: (res) => {
          if (res.code === 400 && res.status === 'Error') {
            this.errorMessage = res.data;
            this.isSubmitted = false;
          } else {

            userDetails['companyName'] = payload.companyName
            userDetails['companyAddress'] = payload.companyAddress
            this.sessionService.setItemIntoSession(
              Constants.sessionConstants.USERDETAILS,
              userDetails
            );
            this.spinner.hide()
          }
        },
        error: (err) => {
          this.spinner.hide()
          console.log('ERROR:', err);
          this.isSubmitted = false;
          this.errorMessage =
            err?.error?.error[0]?.message ||
            'Something went wrong. Please try again later.';
        },
      })
    } else {
      this.companyForm.markAllAsTouched();
    }
  }

  close(): void {
    this.modalService.dismissAll();
  }

  selectedContact(contact: IContactDetails): void {
    // Set the selected contact details to the form
    this.companyForm.patchValue({
      title: contact.title || null,
      firstName: contact?.id !== 0 ? contact.firstName : '',
      lastName: contact.lastName,
      phone: contact.phone,
      contactEmail: contact.email,
      contactSource: contact?.contactSource || 'USER_PROVIDED'
    });
    if(contact.email){
      this.companyForm.get('contactEmail')?.disable();
    }
    else{
      this.companyForm.get('contactEmail')?.enable();
    }
    // store the selected contact
    this.selectedAuthoriserPerson = contact;
  }

  getBrokerCompanies(): void {
    const requestPayload = {
      customerEmail: this.sessionService.getItemFromSession(Constants.sessionConstants.EMAIL)
    }
    const path = 'carbon/v3/get-company?' + new URLSearchParams(requestPayload).toString()
    this.isBrokerCompanyLoading = true
    this.restAPI.getAPI(path, 'domestic').subscribe({
      next: (res) => {
        if (res.code === 200) {
          this.selectedBrokerCompany = requestPayload
          this.companyList = [
            { companyName: '+ Add new company' },
            ...res.data
          ];

          // Get prepopulated company details
          this.getCompanyFromSession();
        } else {
          this.companyList = [{ companyName: '+ Add new company' }]
          this.errorMessage = res.data
        }
        this.isBrokerCompanyLoading = false
      },
      error: (err) => {
        this.companyList = [{ companyName: '+ Add new company' }]
        this.isBrokerCompanyLoading = false
        if(err?.error?.code == 404){
          this.onBrokerCompanyChange({companyName: '+ Add new company'})
          this.companyForm.patchValue({
            brokerCompanyName: '+ Add new company'
          })
        }
      },
    })
  }

  getCompanyFromSession(): void {
    // Pre-select the company from the session
    const selectedCompany = this.sessionService.getItemFromSession(Constants.sessionConstants.ADD_COMPANY_DATA);
    if (selectedCompany) {
      this.selectedBrokerCompany = selectedCompany
      this.companyForm.patchValue({
        brokerCompanyName: selectedCompany.companyName,
        companyType: selectedCompany.companyType,
        companyName: selectedCompany.companyName,
        companyNumber: selectedCompany.companyNumber,
        companyAddress: selectedCompany.companyAddress,
        companyNameManual: null
      });
    }
  }

  get f1(): any {
    return this.companyForm.controls
  }

}
